import { isEmpty } from "lodash";
import { getCompAreaForCostCalc } from "utils/enclosure/components";
import { envelopeRowHeadings, getUpgradeEnvelopeRowLabels } from "utils/upgrades";
import { formatEnvelopeRAreaLines, rsiEffToImperialR } from "./envelopeAuditFormat";

const fieldSelector = (obj, address) => {
    if (address == null || typeof address !== "string") {
        const err = new Error(
            `voltaEnvelopeAudit.fieldSelector: expected string path, got ${address === null ? "null" : typeof address}`
        );
        err.auditHint = { path: address, objType: obj === null ? "null" : typeof obj };
        throw err;
    }
    return address.split(".").reduce((cache, key) => (cache?.[key] == null ? cache : cache[key]), obj);
};

const PONY_WALL_EFF_RVAL_PATH = "wall.ponyWall.ponyWallInsType_effRVal";

const compatibleSubcomps = {
    window: { parents: ["wall", "ceiling", "basement", "crawlspace"] },
    floorHeader: { parents: ["wall", "basement", "crawlspace"] },
    door: { parents: ["wall", "basement", "crawlspace"] },
};

const auditCodeMap = {
    Ceiling: {
        comp: "ceiling",
        fieldPath: "ceilingInsType",
    },
    CeilingFlat: {
        comp: "ceiling",
        fieldPath: "ceilingInsType",
    },
    Wall: {
        comp: "wall",
        fieldPath: "wallInsType",
    },
    Floor: {
        comp: "expFloor",
        fieldPath: "expFloorInsType",
    },
    BasementWall: {
        comp: "basement",
        fieldPath: "wall.intAddedInsType",
    },
    CrawlspaceWall: {
        comp: "crawlspace",
        fieldPath: "wall.crawlspaceWallInsType",
    },
    FloorsAdded: {
        comp: "basement",
        fieldPath: "floor.slabInsType",
    },
    Slab: {
        comp: "slab",
        fieldPath: "floor.slabInsType",
    },
    Window: {
        comp: "window",
        fieldPath: "windowType",
    },
    FloorHeader: {
        comp: "floorHeader",
        fieldPath: "floorHeaderInsType",
    },
    Door: {
        comp: "door",
        fieldPath: "doorType",
    },
};

/** `getCompAreaForCostCalc` compType (differs from envelope `upgradeType`, e.g. basement wall → basementWall). */
const ROW_ID_TO_COST_CALC_TYPE = {
    ceilingWithAttic: "ceiling",
    cathedral: "ceiling",
    wall: "wall",
    expFloor: "expFloor",
    floorHeader: "floorHeader",
    basementWall: "basementWall",
    basementSlab: "basementSlab",
    crawlspace: "crawlspace",
    slab: "slab",
    window: "window",
    skylights: "skylights",
    door: "door",
};

function getAllComponents(components, compType) {
    if (!components) return [];
    return Object.keys(components[compType] || {}).reduce((cache, compKey) => [...cache, `${compType}.${compKey}`], []);
}

function getWindowsOnDoorsAccessors(doorsObj, parentSubPath) {
    if (isEmpty(doorsObj)) return [];
    const accessors = [];
    Object.entries(doorsObj).forEach(([doorKey, doorObj]) => {
        const doorWindows = doorObj?.subcomponents?.window || {};
        if (isEmpty(doorWindows)) return;
        Object.keys(doorWindows).forEach((windowKey) => {
            accessors.push(`${parentSubPath}.door.${doorKey}.subcomponents.window.${windowKey}`);
        });
    });
    return accessors;
}

function getAllSubcomponents(components, compType) {
    const entry = compatibleSubcomps[compType];
    if (!entry?.parents) {
        throw new Error(
            `voltaEnvelopeAudit.getAllSubcomponents: unknown or invalid subcomponent type "${String(compType)}"`
        );
    }
    const { parents } = entry;
    let accessorsToUpdate = [];
    parents.forEach((parentName) => {
        const parentComp = components[parentName] || {};
        if (!isEmpty(parentComp)) {
            Object.entries(parentComp).forEach(([parentKey, parentObj]) => {
                const parentSubPath = `${parentName}.${parentKey}.subcomponents`;
                const subComp = parentObj?.subcomponents?.[compType] || {};
                if (!isEmpty(subComp)) {
                    Object.keys(subComp).forEach((subCompKey) => {
                        accessorsToUpdate = [...accessorsToUpdate, `${parentSubPath}.${compType}.${subCompKey}`];
                    });
                }
                // HPXML Window Area includes glass in doors; HOT2000 stores those as door subcomponents.
                if (compType === "window") {
                    accessorsToUpdate = [
                        ...accessorsToUpdate,
                        ...getWindowsOnDoorsAccessors(parentObj?.subcomponents?.door, parentSubPath),
                    ];
                }
            });
        }
    });
    return accessorsToUpdate;
}

/**
 * HOT2000 door measurements are gross. HPXML Door Area is opaque only
 * (glass is a separate Window). Subtract attached window subcomponents for the audit.
 */
function doorNetOpaqueAreaM2(acc, components) {
    const gross = getCompAreaForCostCalc([acc], components, "door", "matCostArea");
    if (!Number.isFinite(gross) || gross <= 0) return 0;

    const doorComp = fieldSelector(components, acc);
    const doorWindows = doorComp?.subcomponents?.window || {};
    const windowAccessors = Object.keys(doorWindows).map((windowKey) => `${acc}.subcomponents.window.${windowKey}`);
    const glassM2 = windowAccessors.length
        ? getCompAreaForCostCalc(windowAccessors, components, "window", "matCostArea")
        : 0;

    return Math.max(0, gross - (Number.isFinite(glassM2) ? glassM2 : 0));
}

/**
 * Volta nests pony walls in the basement component, but the translator emits them as standalone
 * HPXML `Wall`s, so they get their own audit row. Area is exposed perimeter × pony wall height.
 */
export function buildVoltaPonyWallRow(components = {}, upgrades = {}, upgradePackageId = "") {
    const details = [];

    Object.values(components?.basement || {}).forEach((basementObj) => {
        const { enabled, ponyWallHeight } = basementObj?.wall?.ponyWall || {};
        if (!enabled) return;

        const height = Number(ponyWallHeight);
        const exposedPerimeter = Number(basementObj?.exposedSurfacePerimeter);
        if (!Number.isFinite(height) || !Number.isFinite(exposedPerimeter)) return;

        const areaM2 = height * exposedPerimeter;
        if (areaM2 <= 0) return;

        const upgradeId = basementObj?.upgradePackages?.[upgradePackageId]?.upgradeId || "";
        const upgradeFields = upgrades?.[upgradePackageId]?.upgrades?.[upgradeId]?.fields || null;
        const effRsiRaw = fieldSelector(upgradeFields ?? basementObj, PONY_WALL_EFF_RVAL_PATH);

        details.push({ areaM2, rImp: rsiEffToImperialR(effRsiRaw == null ? null : Number(effRsiRaw)) });
    });

    details.sort((a, b) => b.areaM2 - a.areaM2);

    return {
        id: "ponyWall",
        label: "Pony Walls",
        voltaSummary: formatEnvelopeRAreaLines(details),
        voltaAreaM2: details.reduce((sum, d) => sum + d.areaM2, 0),
    };
}

/**
 * HPXML FoundationWall Area is the exposed wall only (exposed perimeter × height), falling back to
 * the full foundation perimeter when no exposed perimeter is set. Cost calcs use the total perimeter.
 * Height is the total basement wall height, so it includes any pony wall portion (as HPXML does).
 */
function basementWallExposedAreaM2(acc, components) {
    const basementComp = fieldSelector(components, acc);
    const height = Number(basementComp?.wall?.measurements?.height);
    if (!Number.isFinite(height) || height <= 0) return 0;

    const exposedPerimeter = Number(basementComp?.exposedSurfacePerimeter);
    if (Number.isFinite(exposedPerimeter) && exposedPerimeter > 0) return exposedPerimeter * height;

    const { isRectangular, length = 0, width = 0, perimeter = 0 } = basementComp?.floor?.measurements || {};
    const totalPerimeter = isRectangular ? 2 * (length + width) : perimeter;

    return Number.isFinite(totalPerimeter) ? totalPerimeter * height : 0;
}

function envelopeComponentAreaM2(rowId, acc, components, costCalcType) {
    if (rowId === "door") return doorNetOpaqueAreaM2(acc, components);
    if (rowId === "basementWall") return basementWallExposedAreaM2(acc, components);
    return getCompAreaForCostCalc([acc], components, costCalcType, "matCostArea");
}

function getAccessors(components, component) {
    const map = auditCodeMap[component];
    if (!map?.comp) {
        throw new Error(`voltaEnvelopeAudit.getAccessors: no audit mapping for component type "${String(component)}"`);
    }
    return ["Window", "FloorHeader", "Door"].includes(component)
        ? getAllSubcomponents(components, map.comp)
        : getAllComponents(components, map.comp);
}

function filterCeilingAccessors(accessors, components, wantFlat) {
    return accessors.filter((acc) => {
        const ceilingComp = fieldSelector(components, acc);
        const constTypeId = ceilingComp?.constructionType?.id;
        const isFlat = [2, 3].includes(constTypeId);
        return wantFlat ? isFlat : !isFlat;
    });
}

function filterWindowAccessors(accessors, skylightsOnly) {
    return accessors.filter((acc) => (skylightsOnly ? acc.includes("ceiling") : !acc.includes("ceiling")));
}

/**
 * Accessors for an envelope row, after ceiling / window-skylight filters.
 */
export function getFilteredEnvelopeAccessors(components = {}, rowId) {
    const heading = envelopeRowHeadings.find((h) => h.id === rowId);
    if (!heading || rowId === "airTightness") return [];

    const component = heading.type;
    let accessors = getAccessors(components, component);

    if (rowId === "ceilingWithAttic") {
        accessors = filterCeilingAccessors(accessors, components, false);
    } else if (rowId === "cathedral") {
        accessors = filterCeilingAccessors(accessors, components, true);
    } else if (rowId === "window") {
        accessors = filterWindowAccessors(accessors, false);
    } else if (rowId === "skylights") {
        accessors = filterWindowAccessors(accessors, true);
    }

    return accessors;
}

/**
 * Total modeled area (m²) using the same rules as upgrade cost calcs (foundation walls, windows in m², etc.).
 */
export function sumVoltaEnvelopeAreaM2({ components = {} } = {}, rowId) {
    if (rowId === "airTightness") return null;

    const accessors = getFilteredEnvelopeAccessors(components, rowId);
    if (!accessors.length) return 0;

    const costCalcType = ROW_ID_TO_COST_CALC_TYPE[rowId];
    if (!costCalcType) {
        throw new Error(`voltaEnvelopeAudit: no cost-calc type for row "${rowId}"`);
    }

    return accessors.reduce((sum, acc) => sum + envelopeComponentAreaM2(rowId, acc, components, costCalcType), 0);
}

/**
 * Per-component RSI effective R → imperial display R, with areas (m²), largest area first.
 */
export function buildVoltaEnvelopeRAreaDetails({ components = {} } = {}, rowId, upgrades, upgradePackageId) {
    const heading = envelopeRowHeadings.find((h) => h.id === rowId);
    if (!heading || rowId === "airTightness") return [];

    const component = heading.type;
    const map = auditCodeMap[component];
    if (!map?.fieldPath) return [];

    const accessors = getFilteredEnvelopeAccessors(components, rowId);
    const costCalcType = ROW_ID_TO_COST_CALC_TYPE[rowId];
    if (!costCalcType) return [];

    const effRvalFieldPath = rowId === "door" ? `${map.fieldPath}.value` : `${map.fieldPath}_effRVal`;
    const details = [];

    for (const acc of accessors) {
        if (acc == null || typeof acc !== "string") continue;
        const compData = fieldSelector(components, acc);
        if (isEmpty(compData)) continue;

        const upgradeId = compData?.upgradePackages?.[upgradePackageId]?.upgradeId || "";
        const upgradeFields = upgrades?.[upgradePackageId]?.upgrades?.[upgradeId]?.fields || null;

        const areaM2 = envelopeComponentAreaM2(rowId, acc, components, costCalcType);
        if (!Number.isFinite(areaM2) || areaM2 <= 0) continue;

        const effRsiRaw = fieldSelector(upgradeFields ?? compData, effRvalFieldPath);
        const rImp = rsiEffToImperialR(effRsiRaw == null ? null : Number(effRsiRaw));

        if (rowId === "window" || rowId === "skylights") {
            const numIdentical = fieldSelector(compData, "numIdentical");
            details.push(
                ...Array.from({ length: numIdentical }, () => ({
                    areaM2: areaM2 / numIdentical,
                    rImp,
                }))
            );
        } else {
            details.push({
                areaM2,
                rImp,
            });
        }
    }

    details.sort((a, b) => b.areaM2 - a.areaM2);
    return details;
}

export function buildVoltaEnvelopeAuditRows(modelData = {}, upgrades = {}, upgradePackageId = "") {
    const { components = {}, codes = {}, airTightness: airtightness } = modelData;

    return envelopeRowHeadings.flatMap((row) => {
        const { id, content } = row;
        // Pony walls have no heading of their own, but translate to their own HPXML surfaces.
        const extraRows = id === "wall" ? [buildVoltaPonyWallRow(components, upgrades, upgradePackageId)] : [];
        try {
            const areaM2 = sumVoltaEnvelopeAreaM2({ components }, id);

            if (id === "airTightness") {
                const labelResult = getUpgradeEnvelopeRowLabels({
                    components,
                    codes,
                    airtightness,
                    rowId: id,
                    upgradePackage: { [upgradePackageId]: upgrades[upgradePackageId] || {} },
                });
                return [
                    {
                        id,
                        label: content,
                        voltaSummary: labelResult?.label ?? "—",
                        voltaAreaM2: areaM2,
                    },
                    ...extraRows,
                ];
            }

            const rDetails = buildVoltaEnvelopeRAreaDetails({ components }, id, upgrades, upgradePackageId);
            return [
                {
                    id,
                    label: content,
                    voltaSummary: formatEnvelopeRAreaLines(rDetails),
                    voltaAreaM2: areaM2,
                },
                ...extraRows,
            ];
        } catch (err) {
            const wrapped = new Error(
                `Volta envelope row "${id}" (${content}): ${err instanceof Error ? err.message : String(err)}`
            );
            wrapped.cause = err instanceof Error ? err : undefined;
            wrapped.auditContext = { phase: "voltaEnvelope", rowId: id, rowLabel: content };
            throw wrapped;
        }
    });
}

export { auditCodeMap, ROW_ID_TO_COST_CALC_TYPE };
